// reviewShared.jsx — pieces shared by the client's review page, the trip Review tab and
// the Reviews admin: answer inputs, read-only answers, locked trip details, status pills.
import React from "react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** The API returns errors as JSON text ({ message }) — pull out something readable. */
export function errMsg(e) {
  const raw = e?.message || String(e || "");
  try {
    const j = JSON.parse(raw);
    const m = Array.isArray(j.message) ? j.message.join("; ") : j.message;
    return m || j.error || raw;
  } catch {
    return raw || "Something went wrong";
  }
}

export const fmtDay = (d) => {
  if (!d) return "";
  const x = new Date(d);
  return isNaN(x.getTime())
    ? ""
    : x.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};

export const fmtDateTime = (d) => {
  if (!d) return "";
  const x = new Date(d);
  return isNaN(x.getTime())
    ? ""
    : x.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const dateRange = (a, b) => {
  const s = fmtDay(a), e = fmtDay(b);
  if (s && e) return s === e ? s : `${s} – ${e}`;
  return s || e || "";
};

export const QUESTION_TYPES = [
  { value: "rating", label: "Star rating (1–5)" },
  { value: "choice", label: "Pick one" },
  { value: "multi_choice", label: "Pick any" },
  { value: "yes_no", label: "Yes / No" },
  { value: "textarea", label: "Long answer" },
  { value: "text", label: "Short answer" },
  { value: "nps", label: "Score (0–10)" },
];

/** Is a question shown, given the answers so far? (Its "Show only if" rule, if any.) */
export function isShown(q, values = {}) {
  if (!q?.showIf?.questionId) return true;
  const v = values[q.showIf.questionId];
  const anyOf = q.showIf.anyOf || [];
  if (Array.isArray(v)) return v.some((x) => anyOf.includes(String(x)));
  return v !== null && v !== undefined && anyOf.includes(String(v));
}

/** A see-through version of a hex colour, e.g. for selected options. */
export function tint(hex, alpha = 0.1) {
  let h = String(hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return `rgba(15,23,42,${alpha})`;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);

/** Answered questions, without the ones the client never saw because of a "Show only if" rule. */
export function answeredQuestions(questions = []) {
  const values = {};
  questions.forEach((q) => { values[q.id || q.questionId] = q.value; });
  return questions
    .map((q) => ({ ...q, id: q.id || q.questionId }))
    .filter((q) => isShown(q, values) || !isEmpty(q.value));
}

// ─── Status pills ─────────────────────────────────────────────────────────────
export function reviewStatusMeta(r) {
  if (!r) return { label: "Not sent", cls: "bg-slate-100 text-slate-500" };
  if (r.status === "submitted") return { label: "Submitted", cls: "bg-emerald-100 text-emerald-700" };
  if (r.status === "failed") return { label: "Email failed", cls: "bg-rose-100 text-rose-600" };
  if (r.status === "pending") return { label: "Not sent", cls: "bg-slate-100 text-slate-500" };
  if (r.openedAt) return { label: "Opened", cls: "bg-sky-100 text-sky-700" };
  return { label: "Waiting", cls: "bg-amber-100 text-amber-700" };
}

export function ReviewStatusPill({ review }) {
  const m = reviewStatusMeta(review);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

/** Can this feedback be shown publicly? Mirrors the client's consent answer. */
export function publicUseMeta(r) {
  if (!r || r.status !== "submitted") return null;
  if (r.consent === "publish") return { key: "ok", label: "OK to publish", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" };
  if (r.consent === "approval") {
    return r.approvalRecordedAt
      ? { key: "ok", label: "OK to publish (client approved)", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" }
      : { key: "approval", label: "Ask client before publishing", cls: "bg-amber-50 text-amber-700 border-amber-100" };
  }
  if (r.consent === "private") return { key: "private", label: "Private — don't publish", cls: "bg-rose-50 text-rose-600 border-rose-100" };
  return { key: "unknown", label: "No publishing consent", cls: "bg-slate-50 text-slate-500 border-slate-100" };
}

export function PublicUseBadge({ review }) {
  const m = publicUseMeta(review);
  if (!m) return null;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ─── Stars ────────────────────────────────────────────────────────────────────
export const STAR = "M11.48 3.5a.56.56 0 011.04 0l2.13 5.11a.56.56 0 00.48.35l5.52.44c.5.04.7.66.32.99l-4.2 3.6a.56.56 0 00-.18.56l1.28 5.39a.56.56 0 01-.84.61l-4.73-2.89a.56.56 0 00-.59 0l-4.72 2.89a.56.56 0 01-.84-.61l1.28-5.39a.56.56 0 00-.18-.56l-4.2-3.6a.56.56 0 01.32-.99l5.52-.44a.56.56 0 00.48-.35l2.13-5.11z";
const STAR_ON = "#F5B301";
const STAR_OFF = "#E5E7EB";

export function Stars({ value = 0, size = 16 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width={size} height={size} viewBox="0 0 24 24" fill={n <= value ? STAR_ON : STAR_OFF}>
          <path d={STAR} />
        </svg>
      ))}
    </span>
  );
}

// ─── Locked trip details ──────────────────────────────────────────────────────
const LOCK = "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z";

export function LockedDetails({ locked = {} }) {
  const rows = [
    ["Name", locked.name],
    ["Email", locked.email],
    ["Destination", locked.destination],
    ["Service type", locked.serviceType],
    ["Services included", locked.services],
    ["Travel dates", dateRange(locked.startDate, locked.endDate)],
  ].filter(([, v], i) => i < 4 || v); // always show the first four, the rest only when set

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="flex flex-col gap-1 min-w-0">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
          <div
            className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600"
            title="Filled in by our team"
          >
            <span className="flex-1 min-w-0 break-words">{value || "—"}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true">
              <path d={LOCK} />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── One answer input (used one question per page on the client's form) ──────
const RATING_WORDS = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

export function AnswerInput({ q, value, onChange, color = "#111827", large = false }) {
  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 text-base text-slate-700 bg-white outline-none focus:border-slate-400";
  const starSize = large ? 52 : 32;

  switch (q.type) {
    case "rating":
      return (
        <div>
          <div className="flex items-center gap-1 sm:gap-2 -ml-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`}
                className="p-1 rounded-xl transition-transform active:scale-90">
                <svg width={starSize} height={starSize} viewBox="0 0 24 24" fill={value >= n ? STAR_ON : STAR_OFF}>
                  <path d={STAR} />
                </svg>
              </button>
            ))}
          </div>
          <p className="text-sm text-slate-400 mt-2 h-5">{value ? RATING_WORDS[value] : ""}</p>
        </div>
      );

    case "nps":
      return (
        <div className="max-w-[31.5rem]">
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 11 }, (_, n) => (
              <button key={n} type="button" onClick={() => onChange(n)}
                className="w-10 h-10 rounded-xl border text-sm font-semibold transition-all"
                style={value === n ? { background: color, borderColor: color, color: "#fff" } : { borderColor: "#E2E8F0", color: "#475569" }}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1.5">
            <span>0 = not likely</span><span>10 = very likely</span>
          </div>
        </div>
      );

    case "yes_no":
      return <OptionList options={["Yes", "No"]} value={value === "yes" ? "Yes" : value === "no" ? "No" : null}
        onPick={(o) => onChange(o === "Yes" ? "yes" : "no")} color={color} />;

    case "choice":
      return <OptionList options={q.options || []} value={value} onPick={onChange} color={color} />;

    case "multi_choice": {
      const list = Array.isArray(value) ? value : [];
      return (
        <OptionList options={q.options || []} multi selected={list} color={color}
          onPick={(o) => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o])} />
      );
    }

    case "text":
      return (
        <input className={inputCls} value={value || ""} maxLength={500} autoFocus={large}
          onChange={(e) => onChange(e.target.value)} placeholder="Type your answer" />
      );

    case "textarea":
    default:
      return (
        <textarea className={`${inputCls} resize-y`} rows={large ? 6 : 4} value={value || ""} maxLength={5000} autoFocus={large}
          onChange={(e) => onChange(e.target.value)} placeholder="Type your answer (optional)" />
      );
  }
}

function OptionList({ options, value, selected = [], multi = false, onPick, color }) {
  return (
    <div className="flex flex-col gap-2.5">
      {options.map((o) => {
        const on = multi ? selected.includes(o) : value === o;
        return (
          <button key={o} type="button" onClick={() => onPick(o)}
            className="w-full text-left flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all text-[15px]"
            style={on ? { borderColor: color, background: tint(color, 0.07), color: "#0f172a" } : { borderColor: "#E2E8F0", color: "#334155" }}>
            <span className={`flex-shrink-0 w-5 h-5 border-2 flex items-center justify-center ${multi ? "rounded-md" : "rounded-full"}`}
              style={{ borderColor: on ? color : "#CBD5E1", background: on && multi ? color : "transparent" }}>
              {on && (multi
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><path d="M5 13l4 4L19 7" /></svg>
                : <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />)}
            </span>
            <span className="flex-1">{o}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Read-only answers ────────────────────────────────────────────────────────
// questions: [{ id|questionId, label, type, options, showIf, value }]
export function ReviewQuestions({ questions = [] }) {
  const list = answeredQuestions(questions);
  return (
    <div className="flex flex-col gap-5">
      {list.map((q, i) => (
        <div key={q.id} className="flex flex-col gap-1.5">
          <p className="text-sm font-semibold text-slate-800 leading-snug">
            <span className="text-slate-400 mr-1.5">{i + 1}.</span>
            {q.label}
          </p>
          <AnswerView q={q} value={q.value} />
        </div>
      ))}
    </div>
  );
}

function AnswerView({ q, value }) {
  if (isEmpty(value)) return <span className="text-sm text-slate-300">No answer</span>;
  switch (q.type) {
    case "rating":
      return <div className="flex items-center gap-2"><Stars value={value} size={18} /><span className="text-xs text-slate-400">{value}/5 · {RATING_WORDS[value]}</span></div>;
    case "nps":
      return (
        <span className="inline-flex items-center gap-2 text-sm text-slate-700">
          <span className={`px-2 py-0.5 rounded-lg font-semibold ${value >= 9 ? "bg-emerald-100 text-emerald-700" : value >= 7 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-600"}`}>
            {value}
          </span>
          <span className="text-xs text-slate-400">out of 10</span>
        </span>
      );
    case "yes_no":
      return <span className="text-sm text-slate-700 font-medium">{value === "yes" ? "Yes" : "No"}</span>;
    case "choice":
      return <span className="inline-flex w-fit px-2.5 py-1 rounded-lg bg-slate-100 text-sm text-slate-700">{value}</span>;
    case "multi_choice":
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => <span key={v} className="px-2.5 py-1 rounded-lg bg-slate-100 text-sm text-slate-700">{v}</span>)}
        </div>
      );
    case "text":
    case "textarea":
      return <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-xl px-3.5 py-2.5 border border-slate-100">{value}</p>;
    default:
      return <span className="text-sm text-slate-700">{String(value)}</span>;
  }
}

// ─── Consent panel (staff side) ───────────────────────────────────────────────
export function ConsentPanel({ review, onRecordApproval, busy }) {
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState("");
  if (!review || review.status !== "submitted") return null;
  const m = publicUseMeta(review);
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Public use</span>
        <PublicUseBadge review={review} />
      </div>
      <dl className="grid sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        <div><dt className="text-slate-400">Client's answer</dt><dd className="text-slate-700">{review.consentAnswer || "—"}</dd></div>
        <div><dt className="text-slate-400">Show their name as</dt><dd className="text-slate-700">{review.identifyAs || "—"}</dd></div>
        <div><dt className="text-slate-400">OK to contact them</dt><dd className="text-slate-700">{review.contactOk || "—"}</dd></div>
      </dl>
      {review.consentAt && <p className="text-[11px] text-slate-400">Consent given {fmtDateTime(review.consentAt)}. Covers written feedback only — photos or videos need separate consent.</p>}
      {review.approvalRecordedAt && (
        <p className="text-xs text-emerald-700">
          Client approval recorded {fmtDateTime(review.approvalRecordedAt)}
          {review.approvalRecordedBy ? ` by ${review.approvalRecordedBy}` : ""}{review.approvalNote ? ` — ${review.approvalNote}` : ""}
        </p>
      )}
      {m?.key === "approval" && onRecordApproval && (
        open ? (
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <input value={note} onChange={(e) => setNote(e.target.value)} autoFocus
              placeholder="How did they approve? e.g. WhatsApp, 18 Oct"
              className="flex-1 min-w-[220px] px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none bg-white" />
            <button type="button" disabled={busy} onClick={() => onRecordApproval(note.trim())}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
              Save approval
            </button>
            <button type="button" onClick={() => setOpen(false)} className="px-2 py-1.5 text-xs text-slate-500">Cancel</button>
          </div>
        ) : (
          <div>
            <button type="button" onClick={() => setOpen(true)}
              className="mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-200 text-amber-700 hover:bg-amber-50">
              Record client's approval
            </button>
          </div>
        )
      )}
    </div>
  );
}
